import { useCallback } from 'react';

import { guidanceDayKey, type GuidanceSource, type GuidanceSurface } from '@bookora/shared';

import type { GuideTipResponse } from '@/api/generated/model';
import { useAuth } from '@/auth/token-store';
import { useRecordGuidance } from '@/guidance';

/**
 * «Seen» — one TIP `OFFERED` per tip, per staff member, per Tehran day.
 *
 * An impression is what the click-through rate is measured against, and what
 * spends the shared proactive budget the first time a dashboard or inline tip
 * is shown (`applyProfileEvent`). It rides the tour's outbox
 * (`useRecordGuidance`), which is a no-op while impersonating.
 *
 * The server already drops a repeat on the same Tehran day from analytics
 * (`guidanceEventIsLogged`); this log only keeps the panel from sending one per
 * page load. It is an in-memory set backed by `sessionStorage`
 * (`bookora_guide_seen:<staffId>:<day>`), so a reload in the same tab does not
 * send again. Storage is optional: blocked or full storage only means a few
 * extra, harmless events.
 */

const STORAGE_PREFIX = 'bookora_guide_seen';

const memory = new Map<string, Set<string>>();

function storageKey(staffId: string, day: string): string {
  return `${STORAGE_PREFIX}:${staffId}:${day}`;
}

function seenSet(staffId: string, day: string): Set<string> {
  const key = storageKey(staffId, day);
  let set = memory.get(key);
  if (set) return set;
  set = new Set<string>();
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        for (const item of parsed) if (typeof item === 'string') set.add(item);
      }
    }
  } catch {
    // Unreadable storage: start empty.
  }
  memory.set(key, set);
  return set;
}

/**
 * True the first time `itemKey` is marked for this staff member on `now`'s
 * Tehran day; false afterwards. Exported for tests.
 */
export function markSeen(staffId: string, itemKey: string, now: Date = new Date()): boolean {
  const day = guidanceDayKey(now);
  const set = seenSet(staffId, day);
  if (set.has(itemKey)) return false;
  set.add(itemKey);
  try {
    window.sessionStorage.setItem(storageKey(staffId, day), JSON.stringify([...set]));
  } catch {
    // Not persisted; the in-memory set still holds for this tab.
  }
  return true;
}

/** Test seam. */
export function resetImpressionLog(): void {
  memory.clear();
}

export interface ImpressionContext {
  source: Extract<GuidanceSource, 'dashboard' | 'inline' | 'hub'>;
  surface: Extract<GuidanceSurface, 'DASHBOARD' | 'INLINE' | 'HUB'>;
}

/** Returns `seen(tip, rank)`, which records a tip's first impression of the day. */
export function useTipImpressions({
  source,
  surface,
}: ImpressionContext): (tip: GuideTipResponse, rank: number) => void {
  const record = useRecordGuidance();
  const { activeClinic } = useAuth();
  const staffId = activeClinic?.staffId ?? null;
  return useCallback(
    (tip: GuideTipResponse, rank: number) => {
      if (!staffId) return;
      if (!markSeen(staffId, tip.itemKey)) return;
      record({
        type: 'OFFERED',
        kind: 'TIP',
        key: tip.ruleKey,
        instanceKey: tip.instance,
        version: Math.max(1, tip.version),
        source,
        surface,
        rank: Math.max(0, Math.min(20, rank)),
      });
    },
    [record, staffId, source, surface],
  );
}
